import { AlertCircle, Plus, Trash2 } from 'lucide-react'

import {
  V1_TVL_DEAL_TYPE,
  V1_TVL_REWARD_KIND_OPTIONS,
} from '../../../lib/constants'
import type {
  DealFormData,
  V1DepositConfigForm,
  V1TvlWeeklyPoolWeekForm,
} from '../../../lib/types'
import { Button } from '../../ui/button'
import { Checkbox } from '../../ui/checkbox'
import { DropdownSelect } from '../../ui/dropdown-select'
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '../../ui/card'
import { Input } from '../../ui/input'
import { Label } from '../../ui/label'

export const V1_DEPOSIT_FIELD_KEYS = [
  'v1SaleId',
  'v1AllocationSaleId',
  'v1TrackId',
  'v1DepositChainId',
  'v1DepositAddress',
  'depositTokenAddress',
  'depositTokenDecimals',
  'depositTokenName',
  'depositTokenSymbol',
  'v1DepositPoolSize',
  'v1DepositStartTime',
  'v1DepositEndTime',
  'v1RefundStartTime',
  'v1RefundEndTime',
  'v1ClaimChainId',
  'v1ClaimAddress',
  'v1ClaimTokenDecimals',
  'v1ClaimTokenSymbol',
  'v1SaleTokenAddress',
  'v1SaleTokenDecimals',
  'v1SaleTokenName',
  'v1SaleTokenSymbol',
  'v1DepositTokenPrice',
  'v1TvlTargetTVL',
  'v1TvlLockPeriodMonths',
  'v1TvlNativeYieldAPR',
  'v1TvlFees',
  'v1TvlEarlyExitPenalty',
  'v1TvlEarlyExitTokenSymbol',
  'v1TvlRewardKind',
  'v1TvlTokensPerDepositUnit',
  'v1TvlRewardLabel',
  'v1TvlRewardTokenSymbol',
  'v1TvlRewardTokenFdv',
  'v1TvlRewardTokenTotalSupply',
  'v1TvlRewardsPerMonth',
  'v1TvlVestingDescription',
  'purchaseStartDate',
  'purchaseEndDate',
  'mintStartDate',
  'tgeTargetDate',
  'customStartDateTime',
] as const

export type V1DepositFieldKey = typeof V1_DEPOSIT_FIELD_KEYS[number]
export type V1DepositFieldData = Record<V1DepositFieldKey, string> & {
  /** Deposit chains beyond the primary one. */
  v1DepositConfigs: V1DepositConfigForm[]
  requiresKyc: boolean
  v1TvlWeeklyPool: V1TvlWeeklyPoolWeekForm[]
}

/** Non-text parts of the form, patched whole. */
export type V1DepositListPatch = Partial<
  Pick<
    V1DepositFieldData,
    'v1DepositConfigs' | 'requiresKyc' | 'v1TvlWeeklyPool'
  >
>

const EMPTY_WEEK: V1TvlWeeklyPoolWeekForm = {
  baseUsd: '',
  boostTvlThresholdUsd: '',
  boostUsd: '',
}

const WEEK_FIELD_LABELS: Record<keyof V1TvlWeeklyPoolWeekForm, string> = {
  baseUsd: 'Base Pool (USD)',
  boostTvlThresholdUsd: 'Boost TVL Threshold (USD)',
  boostUsd: 'Boosted Pool (USD)',
}

const REWARD_KIND_OPTIONS = [
  { value: '', label: 'None', description: 'The deal declares no rewards.' },
  ...V1_TVL_REWARD_KIND_OPTIONS,
]

const EMPTY_DEPOSIT_CONFIG: V1DepositConfigForm = {
  chainId: '',
  depositAddress: '',
  tokenAddress: '',
  tokenName: '',
  tokenSymbol: '',
  tokenDecimals: '',
  trackId: '',
}

const DEPOSIT_CONFIG_FIELD_LABELS: Record<keyof V1DepositConfigForm, string> = {
  chainId: 'Deposit Chain ID',
  depositAddress: 'Deposit Contract Address',
  tokenAddress: 'Deposit Token Address',
  tokenName: 'Deposit Token Name',
  tokenSymbol: 'Deposit Token Symbol',
  tokenDecimals: 'Deposit Token Decimals',
  trackId: 'Track ID',
}

/** Identifying fields of a stored additional chain; fixed like the above. */
const DEPOSIT_CONFIG_LOCKED_FIELDS: Array<keyof V1DepositConfigForm> = [
  'chainId',
  'depositAddress',
  'tokenAddress',
  'trackId',
]

/** Label for any v1 error key, including `v1DepositConfigs.<i>.<field>`. */
export const getV1DepositFieldLabel = (id: string): string => {
  const [prefix, index, field] = id.split('.')
  if (prefix === 'v1DepositConfigs' && field) {
    const label =
      DEPOSIT_CONFIG_FIELD_LABELS[field as keyof V1DepositConfigForm] || field
    return `Additional Chain ${Number(index) + 1}: ${label}`
  }
  if (prefix === 'v1TvlWeeklyPool' && field) {
    const label =
      WEEK_FIELD_LABELS[field as keyof V1TvlWeeklyPoolWeekForm] || field
    return `Week ${Number(index) + 1}: ${label}`
  }
  if (id === 'v1TvlWeeklyPool') return 'Weekly Pool'
  return V1_DEPOSIT_FIELD_LABELS[id as V1DepositFieldKey] || id
}

/** Identifying fields and addresses, fixed once a deal exists. */
export const V1_DEPOSIT_LOCKED_KEYS: V1DepositFieldKey[] = [
  'v1SaleId',
  'v1AllocationSaleId',
  'v1TrackId',
  'v1DepositChainId',
  'v1DepositAddress',
  'depositTokenAddress',
  'v1ClaimChainId',
  'v1ClaimAddress',
  'v1SaleTokenAddress',
]

/** Multichain deals may still repoint their deposit contracts and tokens. */
const V1_DEPOSIT_EDITABLE_ADDRESS_KEYS: V1DepositFieldKey[] = [
  'v1DepositAddress',
  'depositTokenAddress',
]
const DEPOSIT_CONFIG_EDITABLE_ADDRESS_FIELDS: Array<keyof V1DepositConfigForm> =
  ['depositAddress', 'tokenAddress']

/** Whether a deal's deposit and token addresses stay editable. */
export const isV1MultichainDeal = (
  formData: Pick<DealFormData, 'salesType' | 'v1DepositConfigs'>,
) =>
  formData.salesType === V1_TVL_DEAL_TYPE ||
  (formData.v1DepositConfigs ?? []).length > 0

/** Fields the edit screen keeps read-only; addresses stay open on multichain deals. */
export const getV1DepositLockedKeys = (addressesEditable: boolean) =>
  addressesEditable
    ? V1_DEPOSIT_LOCKED_KEYS.filter(
        (key) => !V1_DEPOSIT_EDITABLE_ADDRESS_KEYS.includes(key),
      )
    : V1_DEPOSIT_LOCKED_KEYS

export const V1_DEPOSIT_FIELD_LABELS: Record<V1DepositFieldKey, string> = {
  v1SaleId: 'Sale ID',
  v1AllocationSaleId: 'Allocation Sale ID',
  v1TrackId: 'Track ID',
  v1DepositChainId: 'Deposit Chain ID',
  v1DepositAddress: 'Deposit Contract Address',
  depositTokenAddress: 'Deposit Token Address',
  depositTokenDecimals: 'Deposit Token Decimals',
  depositTokenName: 'Deposit Token Name',
  depositTokenSymbol: 'Deposit Token Symbol',
  v1DepositPoolSize: 'Deposit Pool Size',
  v1DepositStartTime: 'Deposit Window Start',
  v1DepositEndTime: 'Deposit Window End',
  v1RefundStartTime: 'Refund Window Start',
  v1RefundEndTime: 'Refund Window End',
  v1ClaimChainId: 'Claim Chain ID',
  v1ClaimAddress: 'Claim Contract Address',
  v1ClaimTokenDecimals: 'Claim Token Decimals',
  v1ClaimTokenSymbol: 'Claim Token Symbol',
  v1SaleTokenAddress: 'Sale Token Address',
  v1SaleTokenDecimals: 'Sale Token Decimals',
  v1SaleTokenName: 'Sale Token Name',
  v1SaleTokenSymbol: 'Sale Token Symbol',
  v1DepositTokenPrice: 'Deposit Token Price',
  v1TvlTargetTVL: 'Target TVL',
  v1TvlLockPeriodMonths: 'Lock Period (months)',
  v1TvlNativeYieldAPR: 'Native Yield APR (%)',
  v1TvlFees: 'Fees (%)',
  v1TvlEarlyExitPenalty: 'Early Exit Penalty (%)',
  v1TvlEarlyExitTokenSymbol: 'Early Exit Token Symbol',
  v1TvlRewardKind: 'Reward Model',
  v1TvlTokensPerDepositUnit: 'Reward Tokens Per Deposit Token',
  v1TvlRewardLabel: 'Reward Label',
  v1TvlRewardTokenSymbol: 'Reward Token Symbol',
  v1TvlRewardTokenFdv: 'Reward Token FDV',
  v1TvlRewardTokenTotalSupply: 'Reward Token Total Supply',
  v1TvlRewardsPerMonth: 'Rewards Per Month',
  v1TvlVestingDescription: 'Vesting Description',
  purchaseStartDate: 'Timeline: Deposit Starts',
  purchaseEndDate: 'Timeline: Deposit Ends',
  mintStartDate: 'Timeline: NFT Claiming',
  tgeTargetDate: 'Timeline: First Unlock (TGE)',
  customStartDateTime: 'Timeline: NFT Transferable',
}

export const pickV1DepositFields = (
  formData: Partial<DealFormData>,
): V1DepositFieldData =>
  V1_DEPOSIT_FIELD_KEYS.reduce(
    (acc, key) => {
      acc[key] = String((formData as any)[key] ?? '')
      return acc
    },
    {
      v1DepositConfigs: (formData.v1DepositConfigs ?? []).map((config) => ({
        ...config,
      })),
      requiresKyc: formData.requiresKyc ?? true,
      v1TvlWeeklyPool: (formData.v1TvlWeeklyPool ?? []).map((week) => ({
        ...week,
      })),
    } as V1DepositFieldData,
  )

interface V1DepositFieldsProps {
  data: V1DepositFieldData
  errors: Record<string, string | undefined>
  requestChanges?: Record<string, string>
  onChange: (field: V1DepositFieldKey, value: string) => void
  onPatch: (patch: V1DepositListPatch) => void
  /** Edit mode: IDs, chain IDs and addresses render read-only. */
  locked?: boolean
  /** Edit mode: leading additional chains that are stored, so fixed. */
  lockedConfigCount?: number
  /** TVL deal: also collect the deposit token price and TVL terms. */
  tvl?: boolean
  /** Edit mode: deposit and token addresses stay editable (multichain deals). */
  addressesEditable?: boolean
}

export function V1DepositFields({
  data,
  errors,
  requestChanges,
  onChange,
  onPatch,
  locked = false,
  lockedConfigCount = 0,
  tvl = false,
  addressesEditable = false,
}: V1DepositFieldsProps) {
  const lockedKeys = getV1DepositLockedKeys(addressesEditable)
  const isLocked = (field: V1DepositFieldKey) =>
    locked && lockedKeys.includes(field)

  const field = (
    key: V1DepositFieldKey,
    options: {
      type?: string
      inputMode?: 'numeric' | 'text'
      placeholder?: string
      hint?: string
      label?: string
    } = {},
  ) => {
    const error = errors[key] || requestChanges?.[key]
    const disabled = isLocked(key)
    return (
      <div className="space-y-1" key={key}>
        <Label htmlFor={key}>
          {options.label ?? V1_DEPOSIT_FIELD_LABELS[key]}
        </Label>
        <Input
          id={key}
          type={options.type}
          inputMode={options.inputMode}
          value={data[key]}
          disabled={disabled}
          readOnly={disabled}
          onChange={(event) => onChange(key, event.target.value)}
          placeholder={disabled ? '—' : options.placeholder}
          className={error ? 'border-status-error' : ''}
        />
        {options.hint && !disabled && (
          <p className="text-xs text-neutral-500">{options.hint}</p>
        )}
        {error && (
          <p className="text-status-error text-xs inline-flex items-center gap-1 mt-1">
            <AlertCircle className="w-3 h-3" />
            {error}
          </p>
        )}
      </div>
    )
  }

  const rewardKind = data.v1TvlRewardKind
  const weeks = data.v1TvlWeeklyPool
  const weekField = (
    index: number,
    key: keyof V1TvlWeeklyPoolWeekForm,
    placeholder: string,
  ) => {
    const id = `v1TvlWeeklyPool.${index}.${key}`
    const error = errors[id] || requestChanges?.[id]
    return (
      <div className="space-y-1" key={id}>
        <Label htmlFor={id}>{WEEK_FIELD_LABELS[key]}</Label>
        <Input
          id={id}
          inputMode="numeric"
          value={weeks[index][key]}
          placeholder={placeholder}
          onChange={(event) =>
            onPatch({
              v1TvlWeeklyPool: weeks.map((week, i) =>
                i === index ? { ...week, [key]: event.target.value } : week,
              ),
            })
          }
          className={error ? 'border-status-error' : ''}
        />
        {error && (
          <p className="text-status-error text-xs inline-flex items-center gap-1 mt-1">
            <AlertCircle className="w-3 h-3" />
            {error}
          </p>
        )}
      </div>
    )
  }
  const configs = data.v1DepositConfigs
  const updateConfig = (
    index: number,
    key: keyof V1DepositConfigForm,
    value: string,
  ) =>
    onPatch({
      v1DepositConfigs: configs.map((config, i) =>
        i === index ? { ...config, [key]: value } : config,
      ),
    })

  const configField = (
    index: number,
    key: keyof V1DepositConfigForm,
    options: { inputMode?: 'numeric' | 'text'; placeholder?: string } = {},
  ) => {
    const id = `v1DepositConfigs.${index}.${key}`
    const error = errors[id] || requestChanges?.[id]
    const disabled =
      index < lockedConfigCount &&
      DEPOSIT_CONFIG_LOCKED_FIELDS.includes(key) &&
      !(
        addressesEditable &&
        DEPOSIT_CONFIG_EDITABLE_ADDRESS_FIELDS.includes(key)
      )
    return (
      <div className="space-y-1" key={id}>
        <Label htmlFor={id}>{DEPOSIT_CONFIG_FIELD_LABELS[key]}</Label>
        <Input
          id={id}
          inputMode={options.inputMode}
          value={configs[index][key]}
          disabled={disabled}
          readOnly={disabled}
          onChange={(event) => updateConfig(index, key, event.target.value)}
          placeholder={disabled ? '—' : options.placeholder}
          className={error ? 'border-status-error' : ''}
        />
        {error && (
          <p className="text-status-error text-xs inline-flex items-center gap-1 mt-1">
            <AlertCircle className="w-3 h-3" />
            {error}
          </p>
        )}
      </div>
    )
  }

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <CardTitle>Sale Contract</CardTitle>
          <CardDescription>
            {locked
              ? addressesEditable
                ? 'Sale IDs and chain IDs are fixed for an existing sale. Deposit contract and token addresses can be changed; leave one blank to keep the stored address.'
                : 'Identifiers and addresses are fixed for an existing sale and cannot be edited here.'
              : 'Identifiers assigned by the v1 sale contract. Leave the sale IDs blank if they have not been assigned yet.'}
          </CardDescription>
        </CardHeader>
        <CardContent className="grid gap-5 md:grid-cols-3">
          {field('v1SaleId', { inputMode: 'numeric', placeholder: '986' })}
          {field('v1AllocationSaleId', {
            inputMode: 'numeric',
            placeholder: '988',
          })}
          {field('v1TrackId', { inputMode: 'numeric', placeholder: '0' })}
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Deposit</CardTitle>
          <CardDescription>
            Where and what investors deposit. Times are in UTC.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-5">
          <div className="grid gap-5 md:grid-cols-3">
            {field('v1DepositChainId', {
              inputMode: 'numeric',
              placeholder: '42161',
            })}
            <div className="md:col-span-2">
              {field('v1DepositAddress', { placeholder: '0x...' })}
            </div>
          </div>
          {field('depositTokenAddress', { placeholder: '0x...' })}
          <div className="grid gap-5 md:grid-cols-3">
            {field('depositTokenName', { placeholder: 'USDT' })}
            {field('depositTokenSymbol', { placeholder: 'USDT' })}
            {field('depositTokenDecimals', {
              inputMode: 'numeric',
              placeholder: '6',
            })}
          </div>
          {tvl &&
            field('v1DepositTokenPrice', {
              inputMode: 'numeric',
              placeholder: '0.1',
            })}
          {field('v1DepositPoolSize', {
            inputMode: 'numeric',
            placeholder: '1000000000000 (optional)',
            hint: 'In deposit token base units, e.g. 1,000,000 USDT with 6 decimals is 1000000000000.',
          })}
          <div className="grid gap-5 md:grid-cols-2">
            {field('v1DepositStartTime', { type: 'datetime-local' })}
            {field('v1DepositEndTime', { type: 'datetime-local' })}
          </div>
          <div className="grid gap-5 md:grid-cols-2">
            {field('v1RefundStartTime', {
              type: 'datetime-local',
              label: 'Refund Window Start (optional)',
            })}
            {field('v1RefundEndTime', {
              type: 'datetime-local',
              label: 'Refund Window End (optional)',
            })}
          </div>
          <div className="flex items-start gap-3">
            <Checkbox
              id="requiresKyc"
              checked={data.requiresKyc}
              onCheckedChange={(checked) =>
                onPatch({ requiresKyc: checked === true })
              }
              className="mt-1"
            />
            <div>
              <Label htmlFor="requiresKyc" className="cursor-pointer">
                Requires KYC
              </Label>
              <p
                className={`text-xs ${
                  data.requiresKyc ? 'text-neutral-500' : 'text-status-error'
                }`}
              >
                {data.requiresKyc
                  ? 'Depositors must pass KYC. Enabled by default.'
                  : 'KYC is disabled. Use this only after explicit approval for the sale.'}
              </p>
            </div>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Additional Deposit Chains (optional)</CardTitle>
          <CardDescription>
            For multichain deals: every other chain investors can deposit on,
            each with its own sale contract, deposit token and track ID. The
            deposit above is the primary chain.
            {lockedConfigCount > 0 &&
              (addressesEditable
                ? ' Chains already saved on the deal cannot be removed and keep their chain ID and track ID; their addresses can be changed.'
                : ' Chains already saved on the deal are fixed and cannot be removed.')}
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-5">
          {configs.map((_, index) => (
            <div
              key={index}
              className="space-y-5 rounded-md border border-neutral-200 p-4"
            >
              <div className="flex items-center justify-between">
                <p className="text-sm font-medium">
                  Additional Chain {index + 1}
                </p>
                {index >= lockedConfigCount && (
                  <Button
                    type="button"
                    variant="outline"
                    size="md"
                    className="flex items-center gap-2 bg-transparent px-3"
                    onClick={() =>
                      onPatch({
                        v1DepositConfigs: configs.filter(
                          (__, i) => i !== index,
                        ),
                      })
                    }
                  >
                    <Trash2 className="w-4 h-4" />
                    Remove
                  </Button>
                )}
              </div>
              <div className="grid gap-5 md:grid-cols-3">
                {configField(index, 'chainId', {
                  inputMode: 'numeric',
                  placeholder: '42161',
                })}
                <div className="md:col-span-2">
                  {configField(index, 'depositAddress', {
                    placeholder: '0x...',
                  })}
                </div>
              </div>
              <div className="grid gap-5 md:grid-cols-3">
                <div className="md:col-span-2">
                  {configField(index, 'tokenAddress', {
                    placeholder: '0x...',
                  })}
                </div>
                {configField(index, 'trackId', {
                  inputMode: 'numeric',
                  placeholder: '0',
                })}
              </div>
              <div className="grid gap-5 md:grid-cols-3">
                {configField(index, 'tokenName', { placeholder: 'USDC' })}
                {configField(index, 'tokenSymbol', { placeholder: 'USDC' })}
                {configField(index, 'tokenDecimals', {
                  inputMode: 'numeric',
                  placeholder: '6',
                })}
              </div>
            </div>
          ))}
          <Button
            type="button"
            variant="outline"
            size="md"
            className="flex items-center gap-2 bg-transparent px-5"
            onClick={() =>
              onPatch({
                v1DepositConfigs: [...configs, { ...EMPTY_DEPOSIT_CONFIG }],
              })
            }
          >
            <Plus className="w-4 h-4" />
            Add deposit chain
          </Button>
        </CardContent>
      </Card>

      {tvl && (
        <Card>
          <CardHeader>
            <CardTitle>TVL Terms</CardTitle>
            <CardDescription>
              Lock, yield and reward terms shown on the deal page. Fees and
              early exit penalty default to 0.
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-5">
            <div className="grid gap-5 md:grid-cols-3">
              {field('v1TvlTargetTVL', {
                inputMode: 'numeric',
                placeholder: '2800000',
              })}
              {field('v1TvlLockPeriodMonths', {
                inputMode: 'numeric',
                placeholder: rewardKind === 'percentPerMonth' ? '3' : '0',
                hint: 'Enter 0 for no lock.',
              })}
              {field('v1TvlNativeYieldAPR', {
                inputMode: 'numeric',
                placeholder: 'Optional',
              })}
            </div>
            <div className="grid gap-5 md:grid-cols-3">
              {field('v1TvlFees', { inputMode: 'numeric', placeholder: '0' })}
              {field('v1TvlEarlyExitPenalty', {
                inputMode: 'numeric',
                placeholder: '0',
              })}
              {field('v1TvlEarlyExitTokenSymbol', {
                placeholder: 'Optional',
              })}
            </div>
            {field('v1TvlVestingDescription', {
              placeholder: '40% at TGE, 60% vested over 6 months (optional)',
            })}
          </CardContent>
        </Card>
      )}

      {tvl && (
        <Card>
          <CardHeader>
            <CardTitle>Rewards</CardTitle>
            <CardDescription>
              How a deposit turns into reward tokens. The deal page uses this
              for the reward rate line and the estimated allocation.
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-5">
            <div className="grid gap-5 md:grid-cols-2">
              <div className="space-y-1">
                <Label htmlFor="v1TvlRewardKind">
                  {V1_DEPOSIT_FIELD_LABELS.v1TvlRewardKind}
                </Label>
                <DropdownSelect
                  id="v1TvlRewardKind"
                  ariaLabel={V1_DEPOSIT_FIELD_LABELS.v1TvlRewardKind}
                  value={rewardKind}
                  onChange={(value) => onChange('v1TvlRewardKind', value)}
                  options={REWARD_KIND_OPTIONS}
                  error={errors.v1TvlRewardKind}
                />
              </div>
              {field('v1TvlRewardTokenSymbol', {
                placeholder: rewardKind === 'unquantified' ? 'Optional' : 'ARU',
              })}
            </div>
            {rewardKind === 'percentPerMonth' && (
              <>
                {field('v1TvlRewardsPerMonth', {
                  inputMode: 'numeric',
                  placeholder: '1.08',
                  label: 'Rewards Per Month (% of deposit)',
                  hint: 'Paid over the lock period set in TVL Terms above.',
                })}
                <div className="grid gap-5 md:grid-cols-2">
                  {field('v1TvlRewardTokenFdv', {
                    inputMode: 'numeric',
                    placeholder: '50000000',
                  })}
                  {field('v1TvlRewardTokenTotalSupply', {
                    inputMode: 'numeric',
                    placeholder: '1000000000',
                  })}
                </div>
              </>
            )}
            {rewardKind === 'fixedRatio' && (
              <>
                {field('v1TvlTokensPerDepositUnit', {
                  inputMode: 'numeric',
                  placeholder: '0.909',
                  hint: 'Reward tokens received for 1 deposit token.',
                })}
                <div className="grid gap-5 md:grid-cols-2">
                  {field('v1TvlRewardTokenFdv', {
                    inputMode: 'numeric',
                    placeholder: '10000000 (optional)',
                    label: 'Reward Token FDV (optional)',
                  })}
                  {field('v1TvlRewardTokenTotalSupply', {
                    inputMode: 'numeric',
                    placeholder: '1000000 (optional)',
                    label: 'Reward Token Total Supply (optional)',
                  })}
                </div>
                <p className="text-xs text-neutral-500">
                  Fill in both FDV and total supply to enable the FDV simulator
                  on the deal page.
                </p>
              </>
            )}
            {rewardKind === 'unquantified' &&
              field('v1TvlRewardLabel', {
                placeholder: 'Curated Rewards accrue while deposited',
                hint: 'Shown in place of a reward rate. No allocation estimate is displayed.',
              })}
            {rewardKind === 'weeklyPool' && (
              <div className="space-y-4">
                <p className="text-xs text-neutral-500">
                  One row per 7-day week from the deposit start. Each week's
                  pool is split pro-rata across total TVL on all chains. A boost
                  replaces the base pool once total TVL reaches its threshold.
                  Weeks beyond the list show as TBA.
                </p>
                {errors.v1TvlWeeklyPool && (
                  <p className="text-status-error text-xs inline-flex items-center gap-1">
                    <AlertCircle className="w-3 h-3" />
                    {errors.v1TvlWeeklyPool}
                  </p>
                )}
                {weeks.map((_, index) => (
                  <div
                    key={index}
                    className="space-y-4 rounded-md border border-neutral-200 p-4"
                  >
                    <div className="flex items-center justify-between">
                      <p className="text-sm font-medium">Week {index + 1}</p>
                      <Button
                        type="button"
                        variant="outline"
                        size="md"
                        className="flex items-center gap-2 bg-transparent px-3"
                        onClick={() =>
                          onPatch({
                            v1TvlWeeklyPool: weeks.filter(
                              (__, i) => i !== index,
                            ),
                          })
                        }
                      >
                        <Trash2 className="w-4 h-4" />
                        Remove
                      </Button>
                    </div>
                    <div className="grid gap-5 md:grid-cols-3">
                      {weekField(index, 'baseUsd', '3000')}
                      {weekField(index, 'boostTvlThresholdUsd', 'Optional')}
                      {weekField(index, 'boostUsd', 'Optional')}
                    </div>
                  </div>
                ))}
                <Button
                  type="button"
                  variant="outline"
                  size="md"
                  className="flex items-center gap-2 bg-transparent px-5"
                  onClick={() =>
                    onPatch({ v1TvlWeeklyPool: [...weeks, { ...EMPTY_WEEK }] })
                  }
                >
                  <Plus className="w-4 h-4" />
                  Add week
                </Button>
              </div>
            )}
          </CardContent>
        </Card>
      )}

      <Card>
        <CardHeader>
          <CardTitle>Claim (optional)</CardTitle>
          <CardDescription>
            Contract investors claim from, and the token it pays out. Leave the
            whole section blank for a deposit-only sale.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-5">
          <div className="grid gap-5 md:grid-cols-3">
            {field('v1ClaimChainId', {
              inputMode: 'numeric',
              placeholder: '42161',
            })}
            <div className="md:col-span-2">
              {field('v1ClaimAddress', { placeholder: '0x...' })}
            </div>
          </div>
          <div className="grid gap-5 md:grid-cols-2">
            {field('v1ClaimTokenSymbol', { placeholder: 'USDT' })}
            {field('v1ClaimTokenDecimals', {
              inputMode: 'numeric',
              placeholder: '6',
            })}
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Sale Token (optional)</CardTitle>
          <CardDescription>
            The project token being sold. Leave the section blank for a
            deposit-only sale, or just the address until the token is deployed.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-5">
          {field('v1SaleTokenAddress', { placeholder: '0x... (optional)' })}
          <div className="grid gap-5 md:grid-cols-3">
            {field('v1SaleTokenName', { placeholder: 'SPACE' })}
            {field('v1SaleTokenSymbol', { placeholder: 'SPACE' })}
            {field('v1SaleTokenDecimals', {
              inputMode: 'numeric',
              placeholder: '18',
            })}
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Timeline</CardTitle>
          <CardDescription>
            Milestones shown on the deal page (UTC). Leave blank to omit.
          </CardDescription>
        </CardHeader>
        <CardContent className="grid gap-5 md:grid-cols-2">
          {field('purchaseStartDate', {
            type: 'datetime-local',
            label: 'Deposit Starts',
          })}
          {field('purchaseEndDate', {
            type: 'datetime-local',
            label: 'Deposit Ends',
          })}
          {field('mintStartDate', {
            type: 'datetime-local',
            label: 'NFT Claiming',
          })}
          {field('tgeTargetDate', {
            type: 'datetime-local',
            label: 'First Unlock (TGE)',
          })}
          {field('customStartDateTime', {
            type: 'datetime-local',
            label: 'NFT Transferable',
          })}
        </CardContent>
      </Card>
    </div>
  )
}
